import _, { Dictionary, groupBy } from "lodash"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import {
  CustomFieldDocumentRequirement,
  OperationNodeId,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"

import type {
  ComponentTraceabilityRequestItem,
  EnrichedOperationDto,
  OperationNodeDto,
} from "~/gen/api"
import {
  CustomFieldValueWithDefinitionAndDocumentResponseDto,
  DocumentDto,
  FindOperationTreeByProductIdsResponseDto,
} from "~/gen/api"
import { isProdsiteFieldRequestSubmitted } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProdsiteRequests"
import { getCustomFieldParser } from "~/pages/SupplierPortal/TraceabilityRequests/components/getCustomFieldParser.ts"
import { OperationState } from "~/pages/SupplierPortal/TraceabilityRequests/types.ts"
import { NO_ACTIVITY_NAME } from "~/utils/operations"
import { GetTranslationCallback } from "~/utils/translations"

export type TraceabilityOperation =
  TraceabilityResponses.TraceabilityRequestOperation & {
    isTransferred: boolean
  }

export type OperationItem = {
  activityName: string
  activityId: string
  item: TraceabilityResponses.TraceabilityRequestItem
  branch: readonly number[]
  nodeId: OperationNodeId
  rank: number
  isFirstActivity: boolean
  parentNodeId?: OperationNodeId
  operation: TraceabilityOperation | null
  operationCompletionStatus: OperationCompletionStatus | null
  customFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  extraCustomFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  hiddenCustomFieldDefs: TraceabilityResponses.PublicCustomFieldDef[]
  parentOperation?: TraceabilityOperation
  isInInformationRequest: boolean
  isDuplicateProductionSite?: boolean
}

export type RequestWithOpItem = {
  original: TraceabilityResponses.TraceabilityRequest
  opItems: OperationItem[]
  opItemsByRank: Dictionary<OperationItem[]>
  error?: string
}

function normalizeOperationItemParents(
  operationItems: OperationItem[]
): OperationItem[] {
  const existingNodeIds = new Set(operationItems.map((item) => item.nodeId))

  return operationItems.map((item) => {
    if (!item.parentNodeId || existingNodeIds.has(item.parentNodeId)) {
      return item
    }

    return {
      ...item,
      parentNodeId: undefined,
      parentOperation: undefined,
    }
  })
}

type CustomFieldByActivity = _.Dictionary<
  TraceabilityResponses.PublicCustomFieldDef[]
>

function flattenItemTree(
  item: TraceabilityResponses.TraceabilityRequestItem
): Map<number, TraceabilityResponses.TraceabilityRequestItem> {
  const map = new Map<number, TraceabilityResponses.TraceabilityRequestItem>()
  map.set(item.id, item)
  if ("children" in item && item.children) {
    for (const child of item.children) {
      const childMap = flattenItemTree(child)
      childMap.forEach((v, k) => map.set(k, v))
    }
  }
  return map
}

function isEnrichedOperation(
  operation: OperationNodeDto["operation"]
): operation is EnrichedOperationDto {
  return typeof operation !== "string"
}

function mapEnrichedOpToTraceabilityOp(
  enrichedOp: EnrichedOperationDto,
  isReinforcedTraceability: boolean,
  informationRequestId: number
): TraceabilityOperation {
  const isInIR = informationRequestId === enrichedOp.informationRequestId
  const isTransferred =
    enrichedOp.owner.id !== enrichedOp.contact.userId ||
    isReinforcedTraceability ||
    !isInIR

  return {
    id: enrichedOp.id,
    activity: {
      id: enrichedOp.activityId,
      name: enrichedOp.activityName,
    } as TraceabilityResponses.TraceabilityRequestOperation["activity"],
    reference: enrichedOp.reference ?? null,
    productionSiteId: enrichedOp.productionSiteId ?? null,
    purchaseOrderRef: enrichedOp.purchaseOrderRef ?? null,
    contact: enrichedOp.contact,
    customFieldsValues:
      enrichedOp.customFieldValues as unknown as TraceabilityResponses.CustomFieldValue[],
    owner: enrichedOp.owner,
    doNotKnow: enrichedOp.doNotKnow,
    informationRequestId: enrichedOp.informationRequestId,
    isTransferred,
  }
}

function mapNodeToOperationItem({
  node,
  allNodes,
  customFieldDefByActivity,
  extraCustomFieldDefByOnActivity,
  hiddenCustomFieldDefs,
  itemLookup,
  informationRequestId,
  isReinforcedTraceability,
}: {
  node: OperationNodeDto
  allNodes: OperationNodeDto[]
  customFieldDefByActivity: CustomFieldByActivity
  extraCustomFieldDefByOnActivity: CustomFieldByActivity
  hiddenCustomFieldDefs: CustomFieldByActivity
  itemLookup: Map<number, TraceabilityResponses.TraceabilityRequestItem>
  informationRequestId: number
  isReinforcedTraceability: boolean
}): OperationItem | null {
  const item = itemLookup.get(node.itemId)
  if (!item) return null

  const isFirstActivity = node.type === "product" && node.rank === 0
  const parentNode = allNodes.find((n) => n.selfId === node.parentId)
  const parentNodeId = parentNode?.id as OperationNodeId | undefined

  if (!isEnrichedOperation(node.operation)) {
    return {
      activityName: node.activityName,
      activityId: node.activityId,
      item,
      branch: node.branch,
      nodeId: node.id as OperationNodeId,
      rank: node.rank,
      isFirstActivity,
      parentNodeId,
      operation: null,
      operationCompletionStatus: { progress: 100, isComplete: true },
      customFieldDefs: [],
      extraCustomFieldDefs: [],
      hiddenCustomFieldDefs: [],
      isInInformationRequest: false,
    }
  }

  const operation = mapEnrichedOpToTraceabilityOp(
    node.operation,
    isReinforcedTraceability,
    informationRequestId
  )

  const parentOperation =
    parentNode && isEnrichedOperation(parentNode.operation)
      ? mapEnrichedOpToTraceabilityOp(
          parentNode.operation,
          isReinforcedTraceability,
          informationRequestId
        )
      : undefined

  return {
    activityName: node.activityName,
    activityId: node.activityId,
    item,
    branch: node.branch,
    nodeId: node.id as OperationNodeId,
    rank: node.rank,
    isFirstActivity,
    parentNodeId,
    parentOperation,
    operation,
    operationCompletionStatus: null,
    customFieldDefs: customFieldDefByActivity[node.activityId] || [],
    extraCustomFieldDefs:
      extraCustomFieldDefByOnActivity[node.activityId] || [],
    hiddenCustomFieldDefs: hiddenCustomFieldDefs[node.activityId] || [],
    isInInformationRequest:
      informationRequestId === node.operation.informationRequestId,
  }
}

export interface RequestOperation {
  activityName: string
  item: TraceabilityResponses.TraceabilityRequestItem
  nodeId: OperationNodeId
  parentNodeId?: OperationNodeId
}

export interface OperationsByType {
  type: "material" | "component" | "product"
  item: TraceabilityResponses.TraceabilityRequestItem
  operations: RequestOperation[]
}

const isCustomFieldComplete = (operationItem: OperationItem) => {
  return operationItem.customFieldDefs.every((cfd) => {
    const value = operationItem.operation?.customFieldsValues?.find(
      (x) => x.customFieldDefinitionId === cfd.id
    )?.value
    if (!value || value === "") return false
    const enumValues = cfd.enumValuesTranslated.map((x) => x.originalText)
    const parser = getCustomFieldParser(cfd, enumValues)
    const parsed = parser.safeParse(value)
    return parsed.success
  })
}

export const isProductItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.ProductTraceabilityRequestItem =>
  item.type === "product"

export const isComponentItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.ComponentTraceabilityRequestItem =>
  item.type === "component"

export const isMaterialItem = (
  item: TraceabilityResponses.TraceabilityRequestItem
): item is TraceabilityResponses.MaterialTraceabilityRequestItem =>
  item.type === "material"

export const allChildrenItems = (
  operationItems: OperationItem[],
  nodeId: OperationNodeId
): OperationItem[] => {
  const children = operationItems.filter(
    (item) => item.parentNodeId === nodeId && item.isInInformationRequest
  )
  return children.flatMap((child) => [
    child,
    ...allChildrenItems(operationItems, child.nodeId),
  ])
}

export const loadSavedState = (
  requestId: number,
  userId: number
): Record<OperationNodeId, OperationState> => {
  const savedState = localStorage.getItem(`requestState-${requestId}-${userId}`)
  if (!savedState) return {}
  return JSON.parse(savedState) as Record<OperationNodeId, OperationState> // TODO: should use zod to parse this
}

export const writeSavedState = (
  requestId: number,
  userId: number,
  state: Record<OperationNodeId, OperationState>
) => {
  localStorage.setItem(
    `requestState-${requestId}-${userId}`,
    JSON.stringify(state)
  )
}

export function canSubmitRequest(
  operationItems: OperationItem[],
  user: LibraryResponses.TFGUser | null
): boolean {
  if (!user) return false

  const totalOperationItems = operationItems.length
  const completedOperationItems = operationItems.filter(
    (oi) => oi.operationCompletionStatus?.isComplete
  ).length

  return totalOperationItems === completedOperationItems
}

export function getComponentComposition(
  item: ComponentTraceabilityRequestItem,
  getTranslation: GetTranslationCallback
) {
  const composition = item.children
    .map(
      (m) =>
        `${getTranslation(m.itemInformation.type)} ${
          m.itemInformation.proportion
        }%`
    )
    .join(", ")

  return composition
}

export type RequestWithOpItemFromOperationTree = ReturnType<
  typeof requestsToRequestsWithOp
>[number]

function isComponentNodeWithoutActivity(node: OperationNodeDto): boolean {
  return (
    node.type === "component" &&
    node.operation === "not-entered-yet" &&
    node.activityName === NO_ACTIVITY_NAME
  )
}

export function requestsToRequestsWithOp({
  requests,
  user,
  operationTree,
}: {
  requests?: TraceabilityResponses.TraceabilityRequest[]
  user: LibraryResponses.TFGUser | null
  operationTree: FindOperationTreeByProductIdsResponseDto[]
}): RequestWithOpItem[] {
  if (!user || !operationTree) return []
  return requests
    ? requests.map((request) => {
        const operationTreeNodes = operationTree.find((item) =>
          item.nodes.find((node) => node.itemId === request.item.id)
        )?.nodes
        if (!operationTreeNodes)
          return {
            original: request,
            opItems: [],
            opItemsByRank: {},
            error: "Operation tree not found",
          }

        const customFieldDefByActivity = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) => cfd.forActivityId && cfd.onActivityId === null
          ),
          (cfd) => cfd.forActivityId
        )

        const extraCustomFieldDefByOnActivity = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) =>
              cfd.onActivityId &&
              cfd.forActivityId !== null &&
              cfd.forActivityId !== undefined
          ),
          (cfd) => cfd.onActivityId
        )

        const hiddenCustomFieldDefs = _.groupBy(
          request.customFieldDefinitions.filter(
            (cfd) =>
              cfd.forActivityId &&
              cfd.onActivityId !== null &&
              cfd.onActivityId === request.activityId
          ),
          (cfd) => cfd.forActivityId
        )

        const itemLookup = flattenItemTree(request.item)
        const visibleOperationTreeNodes = operationTreeNodes.filter(
          (node) => !isComponentNodeWithoutActivity(node)
        )

        const opItems = visibleOperationTreeNodes
          .map((node) =>
            mapNodeToOperationItem({
              node,
              allNodes: visibleOperationTreeNodes,
              customFieldDefByActivity,
              extraCustomFieldDefByOnActivity,
              hiddenCustomFieldDefs,
              itemLookup,
              informationRequestId: request.id,
              isReinforcedTraceability: request.reinforcedTraceability,
            })
          )
          .filter((item): item is OperationItem => item !== null)

        const normalizedOpItems = normalizeOperationItemParents(opItems)

        const allCustomFieldValues: CustomFieldValueWithDefinitionAndDocumentResponseDto[] =
          operationTreeNodes.flatMap((node) =>
            isEnrichedOperation(node.operation)
              ? node.operation.customFieldValues
              : []
          )

        const opItemsWithCompletion = normalizedOpItems.map((opItem) => ({
          ...opItem,
          operationCompletionStatus: getOperationCompletionStatus(
            opItem,
            normalizedOpItems,
            allCustomFieldValues
          ),
        }))

        const opItemsByRank = groupBy(opItemsWithCompletion, (oi) => oi.rank)

        return {
          original: request,
          opItems: opItemsWithCompletion,
          opItemsByRank,
        }
      })
    : []
}

export const nodeRequiresRef = (
  parentOpItem: TraceabilityOperation | null,
  opItem: TraceabilityOperation | null
): boolean => {
  if (!opItem) return false
  const doNotKnow = opItem.isTransferred ?? false

  const parentDoNotKnow = parentOpItem?.isTransferred ?? false

  return doNotKnow && !parentDoNotKnow
}

export const isOperationOwner = (
  opItem: TraceabilityOperation,
  user: LibraryResponses.TFGUser | null
) => {
  if (!user) return false
  return !opItem.owner || opItem.owner.id === user.id
}

export const getOpenAndClosedProdsiteFieldRequests = (
  productionSiteFieldRequests:
    | SupplierResponses.ProdsitefieldRequest[]
    | undefined,
  userId: string | undefined
) => {
  const openRequests =
    productionSiteFieldRequests?.filter(
      (pfr) => !isProdsiteFieldRequestSubmitted(pfr, userId)
    ) || []

  const closedRequests =
    productionSiteFieldRequests?.filter((pfr) =>
      isProdsiteFieldRequestSubmitted(pfr, userId)
    ) || []

  return { openRequests, closedRequests }
}

export const getIsValidRef = (
  parentOpItem: TraceabilityOperation | undefined,
  operationItem: TraceabilityOperation | undefined
): boolean => {
  if (!operationItem) return true
  const mandatoryRef = nodeRequiresRef(
    parentOpItem ?? null,
    operationItem ?? null
  )

  return !!(
    !mandatoryRef ||
    (operationItem?.reference !== "" && operationItem?.purchaseOrderRef !== "")
  )
}

export const getIsTransferred = (
  operation: TraceabilityResponses.TraceabilityRequestOperation | undefined,
  isReinforcedTraceability: boolean,
  isInInformationRequest: boolean
): boolean => {
  return (
    operation?.owner.id !== operation?.contact.userId ||
    isReinforcedTraceability ||
    !isInInformationRequest
  )
}

export const getIsReinforcedTraceability = (
  request: RequestWithOpItem,
  operationItem: OperationItem
): boolean => {
  return request.original.reinforcedTraceability && operationItem.rank >= 2
}

export type OperationCompletionStatus = {
  isComplete: boolean
  progress?: number
}

export const getOperationCompletionStatus = (
  operation: OperationItem,
  opItems: OperationItem[],
  customFieldValues: CustomFieldValueWithDefinitionAndDocumentResponseDto[]
): OperationCompletionStatus => {
  if (!operation.operation) {
    return { isComplete: true, progress: 100 }
  }
  const progress = calculateOperationProgress(
    operation.operation.isTransferred ?? false,
    operation,
    opItems,
    customFieldValues
  )

  return { isComplete: progress === 100, progress }
}

export const getIsComplete = (
  operation: OperationItem,
  user: LibraryResponses.TFGUser | null,
  isReinforcedTraceability: boolean
): boolean => {
  if (!operation.operation) return true

  if (!operation.isInInformationRequest) return true

  const isTransferred = getIsTransferred(
    operation.operation,
    isReinforcedTraceability && operation.rank >= 2,
    operation.isInInformationRequest
  )

  const isRefValid = getIsValidRef(
    operation.parentOperation,
    operation.operation
  )
  if (!isOperationOwner(operation.operation, user)) return true

  if (isTransferred) {
    return !!(
      operation.operation?.contact.id &&
      isRefValid &&
      operation.operation?.purchaseOrderRef
    )
  }
  return !!(
    operation.operation?.productionSiteId &&
    isRefValid &&
    isCustomFieldComplete(operation)
  )
}

const getProgressStandardFields = (
  isTransferred: boolean,
  isMandatoryRef: boolean
): string[] => [
  ...(isTransferred ? [] : ["productionSiteId"]),
  ...(isMandatoryRef ? ["reference", "purchaseOrderRef"] : []),
]

export const isCustomFieldValueComplete = (
  fieldValue: unknown,
  documentRequirement: keyof typeof CustomFieldDocumentRequirement,
  document?: DocumentDto | null
): boolean => {
  if (fieldValue === undefined || fieldValue === null || fieldValue === "")
    return false
  if (typeof fieldValue === "string" && fieldValue.trim() === "") return false
  if (Array.isArray(fieldValue) && fieldValue.length === 0) return false
  if (
    documentRequirement === CustomFieldDocumentRequirement.REQUIRED &&
    !document
  )
    return false
  return true
}

const getCompletedCustomFieldsCount = (
  isTransferred: boolean,
  operation: OperationItem,
  extraCustomFieldDefOperationItems: OperationItem[],
  customFieldValues: CustomFieldValueWithDefinitionAndDocumentResponseDto[]
): number => {
  if (isTransferred) return 0

  const completedCustomFields = operation.customFieldDefs.filter((cfd) => {
    const fieldValue = customFieldValues.find(
      (cfv) =>
        cfv.customFieldDefinitionId === cfd.id &&
        cfv.operationId === operation.operation?.id
    )
    if (!fieldValue) return false
    return isCustomFieldValueComplete(
      fieldValue.value,
      cfd.documentRequirement,
      fieldValue.document
    )
  }).length

  const completedExtraCustomFields = extraCustomFieldDefOperationItems
    .flatMap((oi) => {
      return oi.customFieldDefs.filter((cfd) => {
        const fieldValue = customFieldValues.find(
          (cfv) =>
            cfv.customFieldDefinitionId === cfd.id &&
            cfv.operationId === oi.operation?.id
        )
        if (!fieldValue) return false
        return isCustomFieldValueComplete(
          fieldValue.value,
          cfd.documentRequirement,
          fieldValue.document
        )
      }).length
    })
    .filter((num) => num !== undefined)
    .reduce((acc, curr) => acc + curr, 0)

  return completedCustomFields + completedExtraCustomFields
}

export const calculateOperationProgress = (
  isTransferred: boolean,
  operation: OperationItem,
  opItems: OperationItem[],
  customFieldValues: CustomFieldValueWithDefinitionAndDocumentResponseDto[]
): number => {
  const OPERATION_PROGRESS_COMPLETE = 100 as const
  if (!operation.isInInformationRequest) return OPERATION_PROGRESS_COMPLETE

  const extraCustomFieldDefOperationItems = opItems.flatMap((oi) => {
    const extraCustomFieldDefs = operation.extraCustomFieldDefs?.filter(
      (cfd) => cfd.forActivityId === oi.activityId
    )

    if (extraCustomFieldDefs.length > 0) {
      return {
        ...oi,
        customFieldDefs: extraCustomFieldDefs,
      }
    }
    return []
  })
  const totalExtraCustomFields = extraCustomFieldDefOperationItems.reduce(
    (acc, oi) => acc + oi.customFieldDefs.length,
    0
  )

  const totalCustomFields = isTransferred
    ? 0
    : operation.customFieldDefs.length + totalExtraCustomFields

  const isMandatoryRef = nodeRequiresRef(
    operation.parentOperation ?? null,
    operation.operation ?? null
  )
  const totalRequiredFields =
    totalCustomFields +
    getProgressStandardFields(isTransferred, isMandatoryRef).length
  const completedCustomFields = getCompletedCustomFieldsCount(
    isTransferred,
    operation,
    extraCustomFieldDefOperationItems,
    customFieldValues
  )

  const hasProductionSite = Boolean(
    !isTransferred && operation.operation?.productionSiteId
  )
  const hasReference = Boolean(operation.operation?.reference && isMandatoryRef)
  const hasPurchaseOrder = Boolean(
    operation.operation?.purchaseOrderRef && isMandatoryRef
  )

  const standardFieldsChecks = [
    hasProductionSite,
    hasReference,
    hasPurchaseOrder,
  ]

  const completedStandardFieldsCount =
    standardFieldsChecks.filter(Boolean).length

  const totalCompletedFields =
    completedStandardFieldsCount + completedCustomFields

  return totalRequiredFields === 0
    ? OPERATION_PROGRESS_COMPLETE
    : (totalCompletedFields / totalRequiredFields) * 100
}

export const parseRequestOrderDate = (
  request: RequestWithOpItem
): string | undefined => {
  if (request.original.item.type === "product") {
    return request.original.item.itemInformation.date
  }
}

export const parseRequestDueDate = (
  request: RequestWithOpItem
): string | undefined => {
  if (request.original.item.type === "product") {
    return request.original.item.marketLaunchDate?.toLocaleString()
  }
}

export function hasOperation(item: OperationItem): item is Omit<
  OperationItem,
  "operation"
> & {
  operation: TraceabilityOperation
} {
  return item.operation !== null
}

export function assignDuplicateProductionSites(
  operationItems: OperationItem[]
): OperationItem[] {
  const productionSiteCounts: Record<string, number> = {}

  // Count occurrences of each production site ID
  for (const { operation } of operationItems) {
    const productionSiteId = operation?.productionSiteId
    if (!productionSiteId) continue
    productionSiteCounts[productionSiteId] =
      (productionSiteCounts[productionSiteId] || 0) + 1
  }

  // Mark duplicates
  return operationItems.map((opItem) => {
    const productionSiteId = opItem.operation?.productionSiteId
    if (productionSiteId && productionSiteCounts[productionSiteId] > 1) {
      return {
        ...opItem,
        isDuplicateProductionSite: true,
      }
    }
    return opItem
  })
}
